// Focus & Attention — the Mind area's third subsystem track (M3 Phase C).
//
// ⚠️⚠️ **A DERIVED VIEW. IT MINTS NOTHING.** It reads `habit_sessions` and
// `routine_sessions`, whose `xp_earned` **already credits the habit's or routine's
// own domain** — which for a rated Fitness habit is *Fitness*, not Mind. Crediting
// any of it to Mind would double-count and silently inflate Personal, exactly as
// re-crediting breathwork would (see `emotionalRegulation.ts`). Same contract as
// every area rank: one view, no re-mint. **The live proof is that Mind's rank does
// not move when this ships.** Never import `POINTS` here.
//
// That a Fitness session can feed a *Mind* track is deliberate, not a leak: Focus
// is a capacity you exercise anywhere, the way ER reads breathwork that belongs to
// Coherence. It is safe precisely because the track mints nothing.
//
// The subsystem (docs/MIND_CONTENT.md §2) is "aiming accuracy". The act is the
// end-of-session capture added by migration 0212 — the timers were always here;
// the *reading* was the missing half:
//   PRACTICE   — did you do focus work at all? (days with a rated session)
//   DEPTH      — how focused was it? (the mean rating)
//   RESISTANCE — how well did you hold it? (the distraction rate, inverted)
//
// ⚠️ A session is a reading ONLY if it was rated. That is what makes a focus
// session self-selecting: a two-minute toothbrush timer is never rated, so it
// never enters this track. Attention cannot be backfilled from a duration, so the
// track starts empty — an honest zero, like every other unbuilt subsystem.

import { rampedWindow, windowDates } from './trackWindow';

/** How far back the track reads — a *condition* ("lately"), matching ER/Clarity. */
export const FOCUS_WINDOW_DAYS = 30;

/** The shortest denominator the ramp will use — see `rampedWindow`. */
export const FOCUS_RAMP_FLOOR_DAYS = 7;

/** Weights. PRACTICE and DEPTH are the act and its reading; RESISTANCE is the
 *  finer grain (and the noisiest signal), so it is the smallest — mirroring
 *  ER's notice/regulate/outcome and Clarity's check/clarity/naming. */
export const FOCUS_WEIGHTS = { practice: 0.4, depth: 0.4, resistance: 0.2 } as const;

/**
 * Readings needed before DEPTH and RESISTANCE count at all.
 *
 * ⚠️ **The standing lesson from ER and Clarity.** PRACTICE is a *windowed share*
 * (÷ the whole window) and self-limits — one day is 1/7. DEPTH and RESISTANCE are
 * **ratios over the sessions you rated**, a different denominator entirely: without
 * a floor, one flawless session banks 60% of the track and keeps it all window.
 * That is precisely the bug that made ER's outcome read 100. Below this both are
 * *excluded* and their weight redistributed, never counted as zeros.
 */
export const FOCUS_MIN_READINGS = 3;

/** The attention scale. Defined by the capture widget this ships with
 *  (`SessionAttentionCapture`, five buttons) and pinned by a test — the standing
 *  lesson from ER, where breathwork's axes turned out to be 0–100, not 1–5, and
 *  every test agreed with the bug. */
export const FOCUS_SCALE = { min: 1, max: 5 } as const;

/**
 * The distraction rate (per focused hour) at which RESISTANCE reads 0.
 *
 * Six/hour is one break-off every ten minutes — not a focus session in any
 * meaningful sense. Zero reads 100. It is an aggregate rate over the window
 * (total distractions ÷ total rated hours), not a per-session mean, so one short
 * session cannot dominate: a single distraction in a 5-minute session would be
 * 12/hour on its own, but contributes only 1 to the numerator against everything
 * else's hours. A calibration knob like `ER_FULL_GAIN` — retune here.
 */
export const FOCUS_MAX_DISTRACTIONS_PER_HOUR = 6;

/** One day's rated sessions, folded. */
export interface FocusDay {
  date: string; // local YYYY-MM-DD
  /** Sessions that day carrying BOTH a rating and a distraction count. */
  readings: number;
  /** Sum of those sessions' attention ratings (mean = ÷ `readings`). */
  attentionSum: number;
  /** Sum of those sessions' distraction counts. */
  distractions: number;
  /** Sum of those sessions' durations, in seconds. */
  seconds: number;
}

export interface FocusScore {
  /** 0..100 — days you did focus work you cared enough to rate. */
  practicePct: number;
  /** 0..100 — the mean rating, mapped off the 1–5 scale. Reported even below
   *  `FOCUS_MIN_READINGS` (so the UI can show it as provisional), but only folded
   *  into `score` once `hasReadings`. */
  depthPct: number;
  /** 0..100 — the inverted distraction rate. Same provisional rule. */
  resistancePct: number;
  /** 0..100 — the weighted blend. */
  score: number;
  /** 0–10 — the radar axis. */
  level: number;
  windowDays: number;
  practiceDays: number;
  readings: number;
  /** The mean rating on the raw 1–5 scale (0 when unread). */
  meanAttention: number;
  /** Distractions per focused hour (0 when unread) — the raw rate behind
   *  `resistancePct`, for a UI that would rather say "1.4/hr" than "77%". */
  distractionsPerHour: number;
  /** False below `FOCUS_MIN_READINGS` — depth + resistance are then excluded. */
  hasReadings: boolean;
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));
const round1 = (n: number) => Math.round(n * 10) / 10;

/** Days the track is scored over — the shared ramp, at Focus's floor/cap. */
export function focusWindow(
  days: ReadonlyArray<{ date: string }>,
  todayStr: string,
  shift: (ymd: string, n: number) => string,
  maxWindow: number = FOCUS_WINDOW_DAYS,
): number {
  return rampedWindow(days, todayStr, shift, maxWindow, FOCUS_RAMP_FLOOR_DAYS);
}

/** A timed session, narrowed to what Focus reads. Shape shared by
 *  `habit_sessions` and `routine_sessions` since migration 0212. */
export interface FocusSessionLike {
  /** ISO. Absent/null while a session is still running — never a reading.
   *  Both shapes are admitted: `habit_sessions` maps it optional, `routine_sessions`
   *  nullable, and the guard below treats every falsy value the same. */
  completedAt?: string | null;
  durationSeconds: number;
  /** 1–5, or null when the session was never rated (i.e. not focus work). */
  attention: number | null;
  /** >= 0, or null when never captured. */
  distractions: number | null;
}

/**
 * Fold both session sources into one day list.
 *
 * ⚠️ **Both halves of the capture are required.** A session with a rating but no
 * distraction count is NOT a reading: counting its distractions as 0 would silently
 * inflate RESISTANCE, and the capture writes the pair atomically, so a half-written
 * row means an old client or a hand-edit — not a genuinely distraction-free hour.
 *
 * `toYMD` converts the ISO `completedAt` to a LOCAL date, so a late-night session
 * lands on the local day rather than the UTC one.
 */
export function buildFocusDays(
  sessions: ReadonlyArray<FocusSessionLike>,
  toYMD: (iso: string) => string,
): FocusDay[] {
  const byDate = new Map<string, FocusDay>();

  for (const s of sessions) {
    if (!s.completedAt) continue;
    const a = s.attention;
    const d = s.distractions;
    const rated =
      a != null && Number.isFinite(a) && a >= FOCUS_SCALE.min && a <= FOCUS_SCALE.max &&
      d != null && Number.isFinite(d) && d >= 0;
    if (!rated) continue;

    const date = toYMD(s.completedAt);
    let day = byDate.get(date);
    if (!day) {
      day = { date, readings: 0, attentionSum: 0, distractions: 0, seconds: 0 };
      byDate.set(date, day);
    }
    day.readings += 1;
    day.attentionSum += a;
    day.distractions += d;
    day.seconds += Math.max(0, s.durationSeconds || 0);
  }

  return Array.from(byDate.values()).sort((x, y) => x.date.localeCompare(y.date));
}

/** Score the Focus track over the trailing (ramped) window. */
export function scoreFocusAttention(
  days: ReadonlyArray<FocusDay>,
  todayStr: string,
  shift: (ymd: string, n: number) => string,
  maxWindow: number = FOCUS_WINDOW_DAYS,
): FocusScore {
  const windowDays = focusWindow(days, todayStr, shift, maxWindow);
  const window = windowDates(todayStr, windowDays, shift);
  const inWindow = days.filter(d => window.has(d.date));

  // PRACTICE is a windowed share — it self-limits.
  const practiceDays = inWindow.filter(d => d.readings > 0).length;
  const practicePct = Math.round((practiceDays / windowDays) * 100);

  // DEPTH + RESISTANCE are ratios over what you rated — hence the floor.
  const readings = inWindow.reduce((s, d) => s + d.readings, 0);
  const attentionSum = inWindow.reduce((s, d) => s + d.attentionSum, 0);
  const distractions = inWindow.reduce((s, d) => s + d.distractions, 0);
  const seconds = inWindow.reduce((s, d) => s + d.seconds, 0);
  const hasReadings = readings >= FOCUS_MIN_READINGS;

  const meanAttention = readings > 0 ? attentionSum / readings : 0;
  const span = FOCUS_SCALE.max - FOCUS_SCALE.min;
  const depthPct =
    readings > 0 ? Math.round(clamp((meanAttention - FOCUS_SCALE.min) / span, 0, 1) * 100) : 0;

  // An aggregate rate, not a per-session mean, so one short session can't dominate.
  // A rated session of zero length contributes distractions with no hours — the
  // guard keeps that from dividing by zero and reading as a perfect 100.
  const hours = seconds / 3600;
  const distractionsPerHour = hours > 0 ? distractions / hours : distractions > 0 ? FOCUS_MAX_DISTRACTIONS_PER_HOUR : 0;
  const resistancePct =
    readings > 0
      ? Math.round((1 - clamp(distractionsPerHour / FOCUS_MAX_DISTRACTIONS_PER_HOUR, 0, 1)) * 100)
      : 0;

  const w = FOCUS_WEIGHTS;
  const score = hasReadings
    ? Math.round(practicePct * w.practice + depthPct * w.depth + resistancePct * w.resistance)
    : Math.round((practicePct * w.practice) / w.practice);

  return {
    practicePct,
    depthPct,
    resistancePct,
    score,
    level: round1(clamp(score / 10, 0, 10)),
    windowDays,
    practiceDays,
    readings,
    meanAttention: round1(meanAttention),
    distractionsPerHour: round1(distractionsPerHour),
    hasReadings,
  };
}
